<template>
  <div class="base-player-view">
    <div class="player-info">
      <div class="player-info-title">{{ route.query.name }} ({{ route.query.cid }})</div>
    </div>
    <div class="player-container">
      <div class="player-item player-2d">
        <BaseRadarView
          :angle="detailState.northAngle || 0"
          :coordinates="[
            detailState.xxStart,
            detailState.xxEnd,
            detailState.yyStart,
            detailState.yyEnd,
          ]"
          :targets="Object.values(targets)"
          mode="view"
          :dev-id="devId"
          :client-id="clientId"
        />
      </div>
      <div class="player-item player-3d">
        <BasePlayer3D
          :dev-param="devParam"
          :radar-targets="radarTargets"
          :point-clouds="pointClouds"
          :tracking-region="trackingRegion"
          :show-grid="true"
          :show-axis="true"
          :point-size="0.06"
          :point-color="'#00ffff'"
          :stale-timeout-ms="3000"
          :is-replay="true"
        />
      </div>
    </div>

    <div class="player-control">
      <ReplayProgressBar
        v-model:loading="progressLoading"
        v-model:playing="isPlaying"
        :dev-id="devId"
        :client-id="clientId"
        :angle="detailState.northAngle || 0"
        @targets2dChange="handleTargets2dChange"
        @pointCloud3dChange="handlePointCloud3dChange"
        @radarTargetsChange="handleRadarTargetsChange"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'
import type { PointCloud, TrackingRegion } from '@/components/basePlayer3D/types'
import { useRoute } from 'vue-router'
import ReplayProgressBar from './ReplayProgressBar.vue'

defineOptions({ name: 'BasePlayerView' })

type DeviceInfo = {
  installHeight: number // 安装高度
  xxStart: number // 水平方向起点
  xxEnd: number // 水平方向终点
  yyStart: number // 垂直方向起点
  yyEnd: number // 垂直方向终点
  zzStart: number // 垂直方向起点
  zzEnd: number // 垂直方向终点
  northAngle: number // 正北角度
}
interface Props {
  deviceInfo: DeviceInfo // 设备信息 用于展示2D/3D视图的区域
}
const props = withDefaults(defineProps<Props>(), {
  deviceInfo: () => ({
    installHeight: 0,
    xxStart: 0,
    xxEnd: 0,
    yyStart: 0,
    yyEnd: 0,
    zzStart: 0,
    zzEnd: 0,
    northAngle: 0,
  }),
})

/* 路由参数 start */
const route = useRoute()
const devId = ref<string>((route.query.did as string) || '')
const clientId = ref<string>((route.query.cid as string) || '')
/* 路由参数 end */

interface TargetInfo {
  x: number
  y: number
  z: number
  id: number
  displayX: number
  displayY: number
  lastX: number
  lastY: number
}

const progressLoading = ref(false)
const isPlaying = ref(false)
let targets = reactive<Record<number, TargetInfo>>({})

const detailState = ref({
  height: props.deviceInfo.installHeight,
  xxStart: props.deviceInfo.xxStart,
  xxEnd: props.deviceInfo.xxEnd,
  yyStart: props.deviceInfo.yyStart,
  yyEnd: props.deviceInfo.yyEnd,
  zzStart: props.deviceInfo.zzStart,
  zzEnd: props.deviceInfo.zzEnd,
  northAngle: props.deviceInfo.northAngle,
})

/*  ================== 3D视图数据 start ==================  */
const devParam = computed(() => ({
  z_cm: (detailState.value.height as number) ?? 0,
})) // 设备参数
const radarTargets = ref<Array<[number, number, number, number]>>([]) // 雷达目标点数据
const pointClouds = ref<PointCloud[]>([]) // 点云数据
// 雷达跟踪区域（雷达覆盖范围）
const trackingRegion = computed<TrackingRegion>(() => ({
  x_cm_start: (detailState.value.xxStart as number) || 0,
  x_cm_stop: (detailState.value.xxEnd as number) || 0,
  y_cm_start: (detailState.value.yyStart as number) || 0,
  y_cm_stop: (detailState.value.yyEnd as number) || 0,
  z_cm_start: (detailState.value.zzStart as number) || 0,
  z_cm_stop: (detailState.value.zzEnd as number) || 0,
}))
/*  ================== 3D视图数据 start ==================  */

const handleTargets2dChange = (newTargets: Record<number, TargetInfo>) => {
  targets = {} // 清空旧数据
  Object.assign(targets, newTargets)
}

const handlePointCloud3dChange = (newPointClouds: PointCloud[]): void => {
  pointClouds.value = newPointClouds
}

const handleRadarTargetsChange = (newRadarTargets: Array<[number, number, number, number]>) => {
  radarTargets.value = newRadarTargets
}
</script>

<style scoped lang="less">
.base-player-view {
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  padding: 24px;
  min-width: 1060px;

  .player-info {
    margin-bottom: 12px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    .player-info-title {
      font-size: 16px;
      font-weight: 600;
      color: #333;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  .player-container {
    margin-bottom: 12px;
    display: flex;
    gap: 12px;

    .player-item {
      min-width: 500px;
      width: 500px;
      height: 500px;
      overflow: hidden;
    }

    .player-3d {
      flex-grow: 1;
      border-radius: 10px;
    }
  }

  .player-control {
    display: flex;
    flex-direction: column;
    gap: 12px;

    /* 动画效果 */
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }

    @keyframes scaleIn {
      from {
        transform: scale(0.5);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }
  }
}
</style>
